import apiClient from './api';
import {
  Customer,
  CustomerCreate,
  CustomerUpdate,
  CustomerDetail,
  PaginatedResponse,
} from '../types';

const customerService = {
  /**
   * Get paginated list of customers with optional search and filters
   */
  getCustomers: async (
    skip: number = 0,
    limit: number = 50,
    search?: string,
    customerType?: string
  ): Promise<PaginatedResponse<Customer>> => {
    const params: any = { skip, limit };
    if (search) params.search = search;
    if (customerType) params.customer_type = customerType;

    const response = await apiClient.get('/customers', { params });
    return response.data;
  },

  /**
   * Get customer details with policy portfolio
   */
  getCustomer: async (id: string): Promise<CustomerDetail> => {
    const response = await apiClient.get(`/customers/${id}`);
    return response.data;
  },

  /**
   * Create a new customer
   * When source is provided (e.g. 'quotation_inline'), uses the simplified endpoint
   * that doesn't require contacts for corporate customers.
   */
  createCustomer: async (data: CustomerCreate, source?: string): Promise<Customer> => {
    const params: any = {};
    if (source) params.source = source;

    // Use simplified endpoint for inline creation (no contacts required)
    const endpoint = source ? '/customers/simple' : '/customers';
    const response = await apiClient.post(endpoint, data, { params });
    return response.data;
  },

  /**
   * Update customer information (full update with policies/contacts)
   */
  updateCustomer: async (id: string, data: CustomerUpdate): Promise<Customer> => {
    const response = await apiClient.put(`/customers/${id}`, data);
    return response.data;
  },

  /**
   * Update basic customer fields only (no policies/contacts handling).
   * Used by CustomerCompletionDialog to fill missing details.
   */
  updateCustomerBasic: async (id: string, data: any): Promise<Customer> => {
    const response = await apiClient.patch(`/customers/${id}/basic`, data);
    return response.data;
  },

  /**
   * Replace all contacts for a corporate customer.
   * Used by CustomerCompletionDialog to save contacts filled in Stage 2.
   */
  updateCustomerContacts: async (id: string, contacts: any[]): Promise<void> => {
    await apiClient.patch(`/customers/${id}/contacts`, contacts);
  },

  /**
   * Soft delete a customer
   */
  deleteCustomer: async (id: string): Promise<void> => {
    await apiClient.delete(`/customers/${id}`);
  },

  /**
   * Upload file attachment to a policy
   */
  uploadPolicyAttachment: async (
    customerId: string,
    policyId: string,
    file: File
  ): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      `/customers/${customerId}/policies/${policyId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * List attachments for a policy
   */
  listPolicyAttachments: async (
    customerId: string,
    policyId: string
  ): Promise<any[]> => {
    const response = await apiClient.get(
      `/customers/${customerId}/policies/${policyId}/attachments`
    );
    return response.data;
  },

  /**
   * Download a policy attachment
   */
  downloadPolicyAttachment: async (
    customerId: string,
    policyId: string,
    attachmentId: string
  ): Promise<void> => {
    const response = await apiClient.get(
      `/customers/${customerId}/policies/${policyId}/attachments/${attachmentId}`,
      {
        responseType: 'blob',
      }
    );

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'download';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create blob URL and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * View a policy attachment (open in new tab)
   */
  viewPolicyAttachment: async (
    customerId: string,
    policyId: string,
    attachmentId: string
  ): Promise<void> => {
    const response = await apiClient.get(
      `/customers/${customerId}/policies/${policyId}/attachments/${attachmentId}`,
      {
        responseType: 'blob',
      }
    );

    // Create blob URL and open in new tab
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      // Clean up after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } else {
      window.URL.revokeObjectURL(url);
      throw new Error('กรุณาอนุญาตให้เปิดหน้าต่างใหม่ในเบราว์เซอร์');
    }
  },

  /**
   * Delete a policy attachment
   */
  deletePolicyAttachment: async (
    customerId: string,
    policyId: string,
    attachmentId: string
  ): Promise<void> => {
    await apiClient.delete(
      `/customers/${customerId}/policies/${policyId}/attachments/${attachmentId}`
    );
  },

  downloadImportTemplate: async (): Promise<void> => {
    const response = await apiClient.get('/customers/import-template', { responseType: 'blob' });
    _downloadBlob(response.data, 'customer_import_template.xlsx');
  },

  downloadGITemplate: async (): Promise<void> => {
    const response = await apiClient.get('/customers/template-gi', { responseType: 'blob' });
    _downloadBlob(response.data, 'gi_import_template.xlsx');
  },

  downloadLITemplate: async (): Promise<void> => {
    const response = await apiClient.get('/customers/template-li', { responseType: 'blob' });
    _downloadBlob(response.data, 'li_import_template.xlsx');
  },

  importFromFile: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/customers/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importGIFromFile: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/customers/import-gi', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  importLIFromFile: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/customers/import-li', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  exportToExcel: async (params?: { search?: string; customer_type?: string }): Promise<void> => {
    const response = await apiClient.get('/customers/export', { params, responseType: 'blob' });
    _downloadBlob(response.data, 'customers_export.xlsx');
  },

  retryImportRow: async (rawData: Record<string, unknown>): Promise<any> => {
    const formData = new FormData();
    const headers  = Object.keys(rawData).join(',');
    const values   = Object.values(rawData).map(v => String(v ?? '')).join(',');
    const blob     = new Blob([`${headers}\n${values}`], { type: 'text/csv' });
    formData.append('file', blob, 'retry.csv');
    const response = await apiClient.post('/customers/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.results?.[0] ?? response.data;
  },
};

function _downloadBlob(blob: Blob, filename: string): void {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href  = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default customerService;
